/**
 * Where the module's entered data lives: the rate card, the quarter's
 * constraints, the values entered where the platform had nothing, and the
 * decision log.
 *
 * On the platform each is a site note under its own topic — dated,
 * attributed to the person who wrote it, visible to everyone on the site,
 * and append-only, which is what a decision log has to be. The latest note
 * under a topic is the current card or constraints; the log is every note.
 * On the sample, or when the platform refuses the write, the browser keeps
 * a copy so the screen still works; the screen says which it is on.
 */
import type { DataSource } from "../../api/dataSource";
import type { Constraints, DecisionRecord, EnteredInputs, RateCard } from "../../domain/decision";

export const TOPICS = {
  rateCard: "decision.rate-card",
  constraints: "decision.constraints",
  entered: "decision.entered",
  log: "decision.log",
} as const;

const local = (siteId: string, topic: string) => `slt.decision.${siteId}.${topic}`;

function readLocal<T>(siteId: string, topic: string): T | null {
  try {
    const raw = localStorage.getItem(local(siteId, topic));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function writeLocal(siteId: string, topic: string, value: unknown) {
  try {
    localStorage.setItem(local(siteId, topic), JSON.stringify(value));
  } catch {
    /* storage off */
  }
}

async function latest<T>(
  source: DataSource,
  siteId: string,
  topic: string,
): Promise<{ value: T; by?: string; at?: string } | null> {
  try {
    const notes = await source.listNotes(siteId, topic);
    for (const n of notes) {
      try {
        return { value: JSON.parse(n.body) as T, by: n.createdBy, at: n.createdAt };
      } catch {
        /* a note that is not JSON: skip it */
      }
    }
  } catch {
    /* no platform, or no notes there */
  }
  const l = readLocal<T>(siteId, topic);
  return l ? { value: l } : null;
}

async function put(source: DataSource, siteId: string, topic: string, value: unknown): Promise<"platform" | "browser"> {
  writeLocal(siteId, topic, value);
  try {
    await source.addNote(siteId, { topic, category: "recommendation", body: JSON.stringify(value) });
    return "platform";
  } catch {
    return "browser";
  }
}

export async function loadRateCard(source: DataSource, siteId: string): Promise<RateCard | null> {
  const r = await latest<RateCard>(source, siteId, TOPICS.rateCard);
  return r ? { ...r.value, enteredBy: r.value.enteredBy ?? r.by, enteredAt: r.value.enteredAt ?? r.at } : null;
}
export const saveRateCard = (source: DataSource, siteId: string, card: RateCard) =>
  put(source, siteId, TOPICS.rateCard, card);

export async function loadConstraints(source: DataSource, siteId: string): Promise<Constraints | null> {
  return (await latest<Constraints>(source, siteId, TOPICS.constraints))?.value ?? null;
}
export const saveConstraints = (source: DataSource, siteId: string, c: Constraints) =>
  put(source, siteId, TOPICS.constraints, c);

export async function loadEntered(source: DataSource, siteId: string): Promise<EnteredInputs> {
  return (await latest<EnteredInputs>(source, siteId, TOPICS.entered))?.value ?? {};
}
export const saveEntered = (source: DataSource, siteId: string, e: EnteredInputs) =>
  put(source, siteId, TOPICS.entered, e);

/** Every decision ever logged on the site, newest first. */
export async function loadLog(source: DataSource, siteId: string): Promise<DecisionRecord[]> {
  const out: DecisionRecord[] = [];
  try {
    const notes = await source.listNotes(siteId, TOPICS.log);
    for (const n of notes) {
      try {
        const r = JSON.parse(n.body) as DecisionRecord;
        out.push({ ...r, by: r.by || n.createdBy || "unknown", at: r.at || n.createdAt });
      } catch {
        /* not a record */
      }
    }
  } catch {
    /* no platform */
  }
  if (out.length === 0) {
    const l = readLocal<DecisionRecord[]>(siteId, TOPICS.log);
    if (l) out.push(...l);
  }
  return out.sort((a, b) => b.at.localeCompare(a.at));
}

export async function appendLog(
  source: DataSource,
  siteId: string,
  record: DecisionRecord,
): Promise<"platform" | "browser"> {
  const existing = readLocal<DecisionRecord[]>(siteId, TOPICS.log) ?? [];
  writeLocal(siteId, TOPICS.log, [record, ...existing]);
  try {
    await source.addNote(siteId, { topic: TOPICS.log, category: "recommendation", body: JSON.stringify(record) });
    return "platform";
  } catch {
    return "browser";
  }
}
